import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { GoogleOAuthProvider } from '@react-oauth/google';
import ErrorBoundary from './components/ErrorBoundary';
import Notifications from './components/Notifications';
import { Toaster } from './components/ui/sonner';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { syncFCMToken, setupForegroundNotifications, startTokenHeartbeat } from './utils/fcm';
import './index.css';

// Direct imports (no lazy loading)
import Home from './pages/Home';
import Profile from './pages/Profile';
import Contact from './pages/Contact';
import ProviderProfile from './pages/ProviderProfile';
import ManageOrders from './pages/ManageOrders';
import ForgotPassword from './pages/ForgotPassword';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import CategoriesList from './pages/CategoriesList';
import CategoryHub from './pages/CategoryHub';
import CategoryLocation from './pages/CategoryLocation';
import Blog from './pages/Blog';
import ArticlePage from './pages/ArticlePage';
import NotFound from './pages/NotFound';

const queryClient = new QueryClient();

function AppContent({ chatSocket, setChatSocket }) {
  const navigate = useNavigate();
  const location = useLocation();

  // FCM Setup
  useEffect(() => {
    let stopHeartbeat;
    let unsubscribe;

    const setupFCM = async () => {
      try {
        await syncFCMToken();
        stopHeartbeat = await startTokenHeartbeat();
        unsubscribe = setupForegroundNotifications(navigate);
      } catch (err) {
        console.error('[App] Error in FCM setup:', err);
      }
    };

    setupFCM();

    return () => {
      if (unsubscribe) unsubscribe();
      if (stopHeartbeat) stopHeartbeat();
    };
  }, [navigate]);

  // Google Analytics tracking
  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'G-HDE3BPR4FP', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  return (
    <ErrorBoundary>
      <div className="App">
        <Toaster position="top-center" richColors closeButton />
        <Notifications userId={undefined} />
        
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/provider/:providerSlug" element={<ProviderProfile />} />
          <Route path="/manage-orders" element={<ManageOrders />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/categories" element={<CategoriesList />} />
          <Route path="/categories/:slug" element={<CategoryHub />} />
          <Route path="/categories/:slug/:location" element={<CategoryLocation />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:id" element={<ArticlePage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </ErrorBoundary>
  );
}

function App() {
  const [chatSocket, setChatSocket] = useState(null);
  
  // Get your Google Client ID from environment variables
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  // Optional: Add a check to ensure the client ID is set
  if (!googleClientId) {
    console.error('REACT_APP_GOOGLE_CLIENT_ID is not set in environment variables');
  }

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <QueryClientProvider client={queryClient}>
        <Router basename="/">
          <AuthProvider>
            <ThemeProvider>
              <AppContent
                chatSocket={chatSocket}
                setChatSocket={setChatSocket}
              />
            </ThemeProvider>
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
